import { useState, useRef, useCallback } from 'react';
import { Upload, X, File, Image, FileArchive, AlertCircle, CheckCircle2, Loader2, Film } from 'lucide-react';
import { Button } from './ui/button';
import { BACKEND_URL } from '../utils/api';

/**
 * FileUploadZone - Premium drag-and-drop file upload component
 * Supports: files, images, videos, ZIP projects
 * Safety: Read-only mode - files are stored but NOT executed
 */

const ALLOWED_EXTENSIONS = [
    // Code files
    '.js', '.jsx', '.ts', '.tsx', '.py', '.html', '.css', '.scss', '.json', '.md',
    // Images
    '.png', '.jpg', '.jpeg', '.gif', '.svg', '.webp', '.ico',
    // Archives
    '.zip',
    // Config
    '.env', '.yaml', '.yml', '.toml', '.xml'
];

// Video extensions (separate for size validation)
const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.avi', '.mkv', '.m4v'];

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB for regular files
const MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100MB for video files
const MAX_FILES = 20;

const getFileIcon = (filename) => {
    const ext = '.' + filename.toLowerCase().split('.').pop();
    if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'ico'].includes(ext.slice(1))) {
        return <Image className="h-4 w-4 text-emerald-400" />;
    }
    if (VIDEO_EXTENSIONS.includes(ext)) {
        return <Film className="h-4 w-4 text-purple-400" />;
    }
    if (ext === '.zip') {
        return <FileArchive className="h-4 w-4 text-purple-400" />;
    }
    return <File className="h-4 w-4 text-blue-400" />;
};

const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileUploadZone = ({
    onFilesUploaded,
    projectId = null,
    disabled = false,
    compact = false
}) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const [uploadQueue, setUploadQueue] = useState([]);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [errors, setErrors] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);

    const validateFile = (file) => {
        const ext = '.' + file.name.toLowerCase().split('.').pop();
        const isVideo = VIDEO_EXTENSIONS.includes(ext);
        const allAllowed = [...ALLOWED_EXTENSIONS, ...VIDEO_EXTENSIONS];

        if (!allAllowed.includes(ext)) {
            return { valid: false, error: `File type ${ext} not allowed` };
        }

        // Use appropriate size limit for videos vs other files
        const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_FILE_SIZE;
        if (file.size > maxSize) {
            return { valid: false, error: `File exceeds ${formatFileSize(maxSize)} limit` };
        }

        return { valid: true, isVideo };
    };

    const uploadFile = async (file) => {
        const token = localStorage.getItem('token');
        const formData = new FormData();
        formData.append('file', file);
        if (projectId) {
            formData.append('project_id', projectId);
        }

        try {
            const response = await fetch(`${BACKEND_URL}/api/upload`, {
                method: 'POST',
                headers: {
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                },
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `Upload failed: ${response.status}`);
            }

            const result = await response.json();
            return { success: true, data: result };
        } catch (error) {
            return { success: false, error: error.message };
        }
    };

    const processFiles = useCallback(async (files) => {
        const fileArray = Array.from(files).slice(0, MAX_FILES);
        const validFiles = [];
        const newErrors = [];

        // Validate all files first
        for (const file of fileArray) {
            const validation = validateFile(file);
            if (validation.valid) {
                validFiles.push({
                    file,
                    id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                    status: 'pending',
                    progress: 0,
                });
            } else {
                newErrors.push({ filename: file.name, error: validation.error });
            }
        }

        if (newErrors.length > 0) {
            setErrors(prev => [...prev, ...newErrors]);
        }

        if (validFiles.length === 0) return;

        setUploadQueue(validFiles);
        setIsUploading(true);

        // Upload files sequentially to show progress
        const results = [];
        for (const item of validFiles) {
            setUploadQueue(prev =>
                prev.map(f => f.id === item.id ? { ...f, status: 'uploading', progress: 50 } : f)
            );

            const result = await uploadFile(item.file);

            if (result.success) {
                setUploadQueue(prev =>
                    prev.map(f => f.id === item.id ? { ...f, status: 'complete', progress: 100 } : f)
                );
                results.push({
                    filename: item.file.name,
                    size: item.file.size,
                    ...result.data,
                });
            } else {
                setUploadQueue(prev =>
                    prev.map(f => f.id === item.id ? { ...f, status: 'error', error: result.error } : f)
                );
                setErrors(prev => [...prev, { filename: item.file.name, error: result.error }]);
            }
        }

        // Delay to show completion state
        await new Promise(resolve => setTimeout(resolve, 500));

        setUploadedFiles(prev => [...prev, ...results]);
        setUploadQueue([]);
        setIsUploading(false);

        if (results.length > 0 && onFilesUploaded) {
            onFilesUploaded(results);
        }
    }, [projectId, onFilesUploaded]);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) setIsDragOver(true);
    }, [disabled]);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        if (disabled) return;

        const files = e.dataTransfer.files;
        if (files.length > 0) {
            processFiles(files);
        }
    }, [disabled, processFiles]);

    const handleFileSelect = (e) => {
        const files = e.target.files;
        if (files.length > 0) {
            processFiles(files);
        }
        // Reset input so same file can be selected again
        e.target.value = '';
    };

    const removeUploadedFile = (index) => {
        setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const clearError = (index) => {
        setErrors(prev => prev.filter((_, i) => i !== index));
    };

    const clearAllFiles = () => {
        setUploadedFiles([]);
        setErrors([]);
    };

    if (compact && uploadedFiles.length === 0 && !isUploading) {
        return (
            <button
                onClick={() => !disabled && fileInputRef.current?.click()}
                disabled={disabled}
                className="flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-orange-400 
                           bg-slate-800/50 hover:bg-slate-700/50 rounded-lg border border-slate-700/50 
                           hover:border-orange-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <Upload className="h-4 w-4" />
                <span>Attach files</span>
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={ALLOWED_EXTENSIONS.join(',')}
                    onChange={handleFileSelect}
                    className="hidden"
                />
            </button>
        );
    }

    return (
        <div className="space-y-3">
            {/* Drop Zone */}
            <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => !disabled && !isUploading && fileInputRef.current?.click()}
                className={`
                    file-drop-zone relative rounded-xl p-6 cursor-pointer transition-all
                    ${isDragOver ? 'drag-over' : ''}
                    ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                    ${compact ? 'p-4' : 'p-6'}
                `}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={ALLOWED_EXTENSIONS.join(',')}
                    onChange={handleFileSelect}
                    className="hidden"
                    disabled={disabled || isUploading}
                />

                <div className="flex flex-col items-center justify-center text-center">
                    <div className={`
                        rounded-full bg-slate-800/80 p-4 mb-3
                        ${isDragOver ? 'bg-orange-500/20 scale-110' : ''}
                        transition-all duration-200
                    `}>
                        <Upload className={`h-8 w-8 ${isDragOver ? 'text-orange-400' : 'text-slate-400'}`} />
                    </div>

                    <p className="text-sm font-medium text-slate-200 mb-1">
                        {isDragOver ? 'Drop files here' : 'Drag & drop files or click to browse'}
                    </p>
                    <p className="text-xs text-slate-500">
                        Images, code files, or ZIP projects • Max {formatFileSize(MAX_FILE_SIZE)} per file
                    </p>

                    {/* Safety Notice */}
                    <div className="mt-3 flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20">
                        <AlertCircle className="h-3 w-3 text-amber-400" />
                        <span className="text-xs text-amber-300">Files used as AI build context • Never executed on server</span>
                    </div>
                </div>
            </div>

            {/* Upload Progress */}
            {uploadQueue.length > 0 && (
                <div className="space-y-2 animate-fade-in">
                    <div className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                        Uploading {uploadQueue.filter(f => f.status !== 'complete').length} files...
                    </div>
                    {uploadQueue.map((item) => (
                        <div
                            key={item.id}
                            className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-800/60 border border-slate-700/50"
                        >
                            {item.status === 'uploading' && <Loader2 className="h-4 w-4 text-orange-400 animate-spin" />}
                            {item.status === 'complete' && <CheckCircle2 className="h-4 w-4 text-green-400" />}
                            {item.status === 'error' && <AlertCircle className="h-4 w-4 text-red-400" />}
                            {item.status === 'pending' && <File className="h-4 w-4 text-slate-500" />}

                            <span className="flex-1 text-sm text-slate-300 truncate">{item.file.name}</span>
                            <span className="text-xs text-slate-500">{formatFileSize(item.file.size)}</span>

                            {/* Progress bar */}
                            {item.status === 'uploading' && (
                                <div className="w-20 h-1 bg-slate-700 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-orange-500 transition-all duration-300"
                                        style={{ width: `${item.progress}%` }}
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Uploaded Files */}
            {uploadedFiles.length > 0 && (
                <div className="space-y-2 animate-fade-in">
                    <div className="flex items-center justify-between">
                        <div className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                            Attached ({uploadedFiles.length})
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={clearAllFiles}
                            className="text-xs text-slate-500 hover:text-red-400 h-6 px-2"
                        >
                            Clear all
                        </Button>
                    </div>
                    {uploadedFiles.map((file, index) => (
                        <div
                            key={index}
                            className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-800/40 border border-slate-700/30 group"
                        >
                            {getFileIcon(file.filename)}
                            <span className="flex-1 text-sm text-slate-300 truncate">{file.filename}</span>
                            <span className="text-xs text-slate-500">{formatFileSize(file.size)}</span>
                            <button
                                onClick={() => removeUploadedFile(index)}
                                className="opacity-0 group-hover:opacity-100 p-1 hover:bg-slate-700 rounded transition-all"
                            >
                                <X className="h-3 w-3 text-slate-400 hover:text-red-400" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Errors */}
            {errors.length > 0 && (
                <div className="space-y-2 animate-fade-in">
                    {errors.map((err, index) => (
                        <div
                            key={index}
                            className="flex items-center gap-3 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20"
                        >
                            <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
                            <span className="flex-1 text-sm text-red-300">
                                <strong>{err.filename}:</strong> {err.error}
                            </span>
                            <button
                                onClick={() => clearError(index)}
                                className="p-1 hover:bg-red-500/20 rounded transition-all"
                            >
                                <X className="h-3 w-3 text-red-400" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default FileUploadZone;
